import React from 'react';
import Icon from './Icon';

/**
 * Cockpit Button — rust primary with glow-on-hover; quiet secondary;
 * ghost; danger (deliberately not the brand red).
 */
export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  icon?: string;
  iconTrailing?: string;
  nudge?: boolean;
  children?: React.ReactNode;
}

const heights = { sm: 'var(--control-sm)', md: 'var(--control-md)', lg: 'var(--control-lg)' };
const fontSizes = { sm: 'var(--text-sm)', md: 'var(--text-base)', lg: 'var(--text-md)' };
const padX = { sm: '10px', md: '14px', lg: '18px' };

const variants: Record<string, React.CSSProperties> = {
  primary: { background: 'var(--accent)', color: 'var(--text-on-accent)', border: '1px solid transparent' },
  secondary: { background: 'var(--surface-raised)', color: 'var(--text-body)', border: '1px solid var(--border-hairline)' },
  ghost: { background: 'transparent', color: 'var(--text-muted)', border: '1px solid transparent' },
  danger: { background: 'var(--status-down-soft)', color: 'var(--danger-400)', border: '1px solid rgba(229, 72, 77, 0.25)' },
};

export default function Button({
  variant = 'primary',
  size = 'md',
  icon,
  iconTrailing,
  nudge = false,
  disabled = false,
  children,
  onClick,
  style,
  ...rest
}: ButtonProps) {
  const hoverClass = variant === 'primary' ? 'hover-glow' : variant === 'ghost' ? 'hover-wash' : 'hover-lift';
  const iconSize = size === 'sm' ? 14 : 16;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`ck-btn ${hoverClass} pressable ${nudge ? 'hover-nudge' : ''}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '7px',
        height: heights[size],
        padding: `0 ${padX[size]}`,
        borderRadius: 'var(--radius-sm)',
        fontFamily: 'var(--font-ui)',
        fontSize: fontSizes[size],
        fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
        lineHeight: 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        whiteSpace: 'nowrap',
        ...variants[variant],
        ...style,
      }}
      {...rest}
    >
      {icon ? <Icon name={icon} size={iconSize} /> : null}
      {children !== undefined && children !== null ? <span style={{ pointerEvents: 'none' }}>{children}</span> : null}
      {iconTrailing ? <Icon name={iconTrailing} size={iconSize} className={nudge ? 'nudge-target' : ''} /> : null}
    </button>
  );
}
